import React, { useState } from 'react';
import { 
  Building2, GraduationCap, Award, CheckCircle2, Phone, Mail, 
  Calendar, Clock, MapPin, ArrowRight, ShieldCheck, Sparkles, 
  BookOpen, Users, FileText, Download, Printer, ExternalLink,
  ChevronRight, AlertCircle, Briefcase, Star, MessageSquare
} from 'lucide-react';
import { INSTITUTION_INFO } from '../data/portalData';
import { SocialIconLinks } from './SocialLinks';

export const AdmissionAdvert = ({ setActiveView }) => {
  const [selectedDiscipline, setSelectedDiscipline] = useState('ALL');

  // Categorised Programmes as presented on the Official Admission Advert Circular
  const disciplines = [
    { id: 'ALL', label: 'All Disciplines' },
    { id: 'BA', label: 'Business Administration' },
    { id: 'PA', label: 'Public Administration' },
    { id: 'AF', label: 'Accounting & Finance' }
  ];

  const advertProgrammes = [
    // Business Administration
    {
      discipline: 'BA',
      disciplineTitle: 'Business Administration',
      title: 'Doctor of Business Administration (DBA)',
      level: 'Doctoral Track',
      duration: '36 Months (Executive Weekend)',
      code: 'DBA',
      badge: 'Terminal Executive Degree',
      summary: 'Advanced executive doctoral programme focusing on applied business research, strategic corporate transformation, board governance, and high-impact enterprise leadership.',
      target: 'CEOs, Managing Directors, C-Suite Executives & Senior Directors'
    },
    {
      discipline: 'BA',
      disciplineTitle: 'Business Administration',
      title: 'Master of Business Administration (MBA)',
      level: "Master's Track",
      duration: '24 Months (Part-Time / Executive)',
      code: 'MBA',
      badge: 'Flagship Executive Master',
      summary: 'Rigorous management education blending analytics, organizational psychology, finance, and global market positioning for advancing managers and business leaders.',
      target: 'Mid-to-Senior Managers, Departmental Heads & Business Founders'
    },
    {
      discipline: 'BA',
      disciplineTitle: 'Business Administration',
      title: 'Postgraduate Diploma in Business Administration (PGD)',
      level: 'Postgraduate Track',
      duration: '12 Months (2 Semesters)',
      code: 'PGD BA',
      badge: 'Direct MBA Pathway',
      summary: 'Bridging foundation for holders of HND or third-class first degrees seeking rigorous re-tooling in modern enterprise administration and academic progression.',
      target: 'HND Holders, Degree Holders in Non-Business Disciplines'
    },
    {
      discipline: 'BA',
      disciplineTitle: 'Business Administration',
      title: 'Diploma in Business Administration',
      level: 'Diploma Track',
      duration: '18 Months',
      code: 'DIP BA',
      badge: 'Undergraduate Diploma',
      summary: 'Practical immersion in core business functions, operations management, communication, and business mathematics for career starters.',
      target: 'Working Staff seeking professional qualification credentials'
    },
    {
      discipline: 'BA',
      disciplineTitle: 'Business Administration',
      title: 'Certificate in Business Administration',
      level: 'Certificate Track',
      duration: '6 Months',
      code: 'CERT BA',
      badge: 'Professional Proficiency',
      summary: 'Fast-track competency building covering fundamental enterprise operations, basic bookkeeping, client relationship management, and commercial ethics.',
      target: 'Office Administrators, Supervisors & Business Apprentices'
    },

    // Public Administration
    {
      discipline: 'PA',
      disciplineTitle: 'Public Administration',
      title: 'Doctor of Public Administration (DPA)',
      level: 'Doctoral Track',
      duration: '36 Months (Executive)',
      code: 'DPA',
      badge: 'Public Sector Doctoral Prestige',
      summary: 'Designed for top-tier public servants, policy makers, and administrators to interrogate public policy dynamics, administrative law, intergovernmental relations, and statecraft.',
      target: 'Permanent Secretaries, Directors-General, Commissioners & MDA Heads'
    },
    {
      discipline: 'PA',
      disciplineTitle: 'Public Administration',
      title: 'Executive Master of Public Administration (EMPA)',
      level: "Master's Track",
      duration: '24 Months (Executive Weekend)',
      code: 'EMPA',
      badge: 'Executive Governance Master',
      summary: 'Specialised leadership curricula covering public sector budgeting, e-governance, policy formulation, procurement integrity, and strategic public enterprise management.',
      target: 'Deputy Directors, Assistant Directors, LGA Leaders & NGO Directors'
    },
    {
      discipline: 'PA',
      disciplineTitle: 'Public Administration',
      title: 'Professional Diploma in Public Administration',
      level: 'Diploma Track',
      duration: '12-18 Months',
      code: 'PDIP PA',
      badge: 'Civil Service Advancement',
      summary: 'Structured competency development for civil service cadre enhancement, office protocol, public records management, and civil service regulations.',
      target: 'Civil Service Cadres, Administrative Officers & Parastatal Staff'
    },
    {
      discipline: 'PA',
      disciplineTitle: 'Public Administration',
      title: 'Professional Certificate in Public Administration',
      level: 'Certificate Track',
      duration: '6 Months',
      code: 'PCERT PA',
      badge: 'Administrative Proficiency',
      summary: 'Focused short-course imparting essential skills in administrative drafting, official records keeping, ethics in governance, and public service protocols.',
      target: 'Secretarial Staff, Clerical Officers & Administrative Assistants'
    },

    // Accounting & Finance
    {
      discipline: 'AF',
      disciplineTitle: 'Accounting & Finance',
      title: 'Doctor of Accounting and Finance',
      level: 'Doctoral Track',
      duration: '36 Months (Executive)',
      code: 'DAF',
      badge: 'Doctoral Financial Pedigree',
      summary: 'Rigorous empirical doctoral studies into financial econometrics, forensic accounting, capital structure, tax policy, and corporate valuation under global frameworks.',
      target: 'Chief Financial Officers, Senior Audit Partners & Academic Researchers'
    },
    {
      discipline: 'AF',
      disciplineTitle: 'Accounting & Finance',
      title: 'Executive Master of Accounting and Finance',
      level: "Master's Track",
      duration: '24 Months (Executive Weekend)',
      code: 'EMAF',
      badge: 'Executive Finance Master',
      summary: 'Strategic mastery of IFRS reporting, public sector IPSAS accounting, fintech, risk management, taxation strategy, and corporate treasury management.',
      target: 'Financial Controllers, Chief Accountants, Treasurers & Auditors'
    },
    {
      discipline: 'AF',
      disciplineTitle: 'Accounting & Finance',
      title: 'Professional Diploma in Accounting and Finance',
      level: 'Diploma Track',
      duration: '12-18 Months',
      code: 'PDIP AF',
      badge: 'ICAN Partnership Route',
      summary: 'Applied accounting principles, computerized accounting software, tax computations, and audit procedures geared towards ICAN and CIBN exemptions.',
      target: 'Accounts Officers, Cashiers, Microfinance Staff & Tax Officers'
    },
    {
      discipline: 'AF',
      disciplineTitle: 'Accounting & Finance',
      title: 'Professional Certificate in Accounting and Finance',
      level: 'Certificate Track',
      duration: '6 Months',
      code: 'PCERT AF',
      badge: 'Financial Essentials',
      summary: 'Practical grounding in bookkeeping, payroll computation, basic spreadsheet auditing, and statutory deductions (PAYE, VAT, WHT).',
      target: 'SME Bookkeepers, Accounts Clerks & Billing Officers'
    }
  ];

  const filteredProgrammes = selectedDiscipline === 'ALL' 
    ? advertProgrammes 
    : advertProgrammes.filter(p => p.discipline === selectedDiscipline);

  const admissionRequirements = [
    {
      letter: 'a',
      category: "Master's Programmes (MBA, EMPA, EMAF)",
      badge: "Master's Entry Criteria",
      criteria: "A good first degree in any discipline from a recognised University, plus at least 3 years of verifiable public or private service experience.",
      details: "Candidates with professional certifications (ICAN, CIBN, NIM, CIPM, CITN) have an added advantage for direct shortlisting."
    },
    {
      letter: 'b',
      category: "Doctoral Programmes (DBA, DPA, DAF)",
      badge: "Doctoral Entry Criteria",
      criteria: "A Master's degree in any discipline from a recognised University with satisfactory academic standing.",
      details: "Applicants must submit a brief Concept Note (Statement of Research Intent) detailing their anticipated executive research problem."
    },
    {
      letter: 'c',
      category: "Postgraduate Diploma (PGD)",
      badge: "PGD Pathway",
      criteria: "A recognised first degree in a non-related field OR a Third Class honours degree from an accredited University.",
      details: "Acts as an accredited foundation bridge for career transit into business leadership and qualification for MBA intake."
    },
    {
      letter: 'd',
      category: "Higher National Diploma (HND) Holders",
      badge: "Polytechnic Transition",
      criteria: "A Higher National Diploma (HND) with minimum of Lower Credit in a relevant discipline from a recognised polytechnic or monotechnic.",
      details: "Eligible for admission into the Postgraduate Diploma (PGD) and subsequent advancement into Executive Master's programmes."
    },
    {
      letter: 'e',
      category: "Certificate & Diploma Programmes",
      badge: "Foundational & Diploma Entry",
      criteria: "The Senior School Certificate Examination (SSCE / WAEC / NECO / NABTEB / GCE) with five (5) credit passes, including English Language and Mathematics.",
      details: "Working professionals with relevant administrative exposure and mature age exemptions may receive special Senate consideration."
    }
  ];

  const handlePrint = () => {
    window.print();
  };

  return (
    <div className="bg-slate-50 min-h-screen py-10 font-sans print:bg-white print:py-0">
      
      {/* Top Floating Action Bar (Hidden on print) */}
      <div className="max-w-5xl mx-auto px-4 mb-6 flex flex-wrap items-center justify-between gap-3 print:hidden">
        <button
          onClick={() => setActiveView('home')}
          className="inline-flex items-center text-xs font-bold text-[#0B2644] hover:text-[#7A470E] transition-colors font-sc"
        >
          <ChevronRight className="w-4 h-4 rotate-180 mr-1" />
          <span>Return to Portal Home</span>
        </button>

        <div className="flex items-center space-x-2.5">
          <button
            onClick={handlePrint}
            className="px-3.5 py-1.5 rounded-xl border border-slate-300 bg-white hover:bg-slate-50 text-slate-700 text-xs font-bold shadow-sm transition-all flex items-center space-x-1.5 font-sc cursor-pointer"
          >
            <Printer className="w-3.5 h-3.5 text-[#0B2644]" />
            <span>Print Advert</span>
          </button>

          <a
            href={`https://wa.me/2348033380401?text=Hello%20AKSU%20Business%20School%20Admissions,%20I%20am%20inquiring%20about%20the%202025/2026%20Session%20Admission%20Advert.`}
            target="_blank"
            rel="noopener noreferrer"
            className="px-3.5 py-1.5 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold shadow-sm transition-all flex items-center space-x-1.5 font-sc"
          >
            <MessageSquare className="w-3.5 h-3.5" />
            <span>WhatsApp Desk</span>
          </a>

          <button
            onClick={() => setActiveView('apply')}
            className="px-4 py-1.5 rounded-xl bg-[#7A470E] hover:bg-[#5C3306] text-white text-xs font-black shadow-md hover:scale-105 transition-all flex items-center space-x-1.5 font-sc cursor-pointer"
          >
            <Award className="w-3.5 h-3.5" />
            <span>Apply Now (₦25,000)</span>
          </button>
        </div>
      </div>

      {/* Main Premium Advert Document Container */}
      <div className="max-w-5xl mx-auto px-3 sm:px-6">
        <article className="bg-white rounded-2xl sm:rounded-3xl shadow-xl border-2 sm:border-4 border-[#7A470E] overflow-hidden print:shadow-none print:border-2 print:rounded-none">

          {/* ================= SECTION 1: PRESTIGE NAVY & GOLD BANNER ================= */}
          <header className="bg-gradient-to-b from-[#06182B] via-[#0B2644] to-[#06182B] text-white p-4 sm:p-10 border-b-4 border-[#7A470E] relative overflow-hidden">
            {/* Subtle luxury glow effect */}
            <div className="absolute top-0 right-0 w-96 h-96 bg-[#7A470E]/10 rounded-full blur-3xl pointer-events-none" />
            <div className="absolute bottom-0 left-0 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl pointer-events-none" />

            <div className="relative z-10 flex flex-col items-center text-center">
              
              {/* Crest and Emblems Row */}
              <div className="flex items-center justify-center space-x-4 sm:space-x-8 mb-6">
                <div className="relative">
                  <img 
                    src="/Logo.png" 
                    alt="Akwa Ibom State University Crest" 
                    className="w-20 h-20 sm:w-24 sm:h-24 object-contain drop-shadow-[0_4px_12px_rgba(203,163,41,0.35)]" 
                  />
                </div>
              </div>

              {/* Institution Title */}
              <h2 className="text-sm sm:text-base tracking-[0.25em] uppercase font-bold text-slate-300 font-sc">
                Akwa Ibom State University
              </h2>
              <h1 className="text-3xl sm:text-5xl lg:text-6xl font-black text-[#B87A28] font-sc tracking-wide mt-1 mb-2">
                BUSINESS SCHOOL
              </h1>
              <p className="text-xs sm:text-sm font-black tracking-[0.35em] uppercase text-white font-sc border-t border-b border-[#7A470E]/40 py-1 px-4 inline-block mt-1">
                KNOWLEDGE · ENTERPRISE · LEADERSHIP
              </p>

              <div className="mt-4 flex flex-wrap items-center justify-center gap-2 text-[11px] text-slate-300 font-mono">
                <span className="bg-[#030F1C] px-3 py-1 rounded-full border border-[#7A470E]/40 text-white font-bold flex items-center">
                  <ShieldCheck className="w-3.5 h-3.5 mr-1 text-[#B87A28]" />
                  Official Admission Circular
                </span>
                <span className="bg-[#030F1C] px-3 py-1 rounded-full border border-[#7A470E]/40 text-slate-200">
                  Domain: {INSTITUTION_INFO.domain}
                </span>
              </div>

            </div>
          </header>

          {/* ================= SECTION 2: ADMISSIONS GOLD RIBBON ================= */}
          <div className="bg-[#7A470E] text-white py-4 px-6 sm:px-10 border-b-2 border-[#5C3306] text-center shadow-inner">
            <h3 className="text-2xl sm:text-3xl lg:text-4xl font-black font-sc tracking-wide">
              ADMISSIONS — 2025/2026 SESSION
            </h3>
            <p className="text-xs sm:text-sm font-extrabold uppercase tracking-widest font-sc mt-1 text-amber-100">
              NOW OPEN · POSTGRADUATE, PROFESSIONAL & DIPLOMA PROGRAMMES
            </p>
          </div>

          {/* ================= SECTION 3: EXECUTIVE PREMISE & AUDIENCE ================= */}
          <section className="bg-amber-50/50 p-6 sm:p-10 border-b-2 border-slate-200">
            <div className="max-w-4xl mx-auto text-center space-y-4">
              <div className="inline-flex items-center space-x-2 text-xs font-black uppercase tracking-widest text-[#0B2644] bg-white px-3 py-1 rounded-lg border border-[#7A470E] shadow-sm font-sc">
                <Briefcase className="w-3.5 h-3.5 text-[#7A470E]" />
                <span>Executive Academic Structure</span>
              </div>
              
              <h4 className="text-xl sm:text-2xl font-black text-[#0B2644] font-sc">
                AN EXECUTIVE PROGRAMME
              </h4>
              
              <p className="text-slate-700 text-sm sm:text-base leading-relaxed text-justify sm:text-center">
                The <strong className="text-[#0B2644]">AKSU Business School</strong> is designed as an 
                executive programme for <span className="font-bold text-[#0B2644]">business executives</span>, 
                <span className="font-bold text-[#0B2644]"> captains of industry</span>, 
                <span className="font-bold text-[#0B2644]"> heads of government MDAs</span>, 
                <span className="font-bold text-[#0B2644]"> political appointees</span>, and 
                other <span className="font-bold text-[#0B2644]">senior executives</span>. 
                It is run <span className="underline decoration-[#7A470E] decoration-2 font-bold">part-time</span> and 
                structured for individuals who may not have the time to enrol in a regular, full-time academic programme.
              </p>

              {/* Executive Audience Icons */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 pt-4 text-left">
                <div className="translucent-card-light p-3 rounded-2xl shadow-sm flex items-center space-x-2.5 hover:scale-[1.02] transition-all">
                  <div className="w-8 h-8 rounded-xl bg-[#0B2644] text-white flex items-center justify-center font-bold text-xs shrink-0 font-sc">
                    CEO
                  </div>
                  <div>
                    <span className="text-xs font-bold text-slate-900 block font-sc">Corporate Leaders</span>
                    <span className="text-[10px] text-slate-500">Board Directors & MDs</span>
                  </div>
                </div>

                <div className="translucent-card-light p-3 rounded-2xl shadow-sm flex items-center space-x-2.5 hover:scale-[1.02] transition-all">
                  <div className="w-8 h-8 rounded-xl bg-[#0B2644] text-white flex items-center justify-center font-bold text-xs shrink-0 font-sc">
                    MDA
                  </div>
                  <div>
                    <span className="text-xs font-bold text-slate-900 block font-sc">Public Servants</span>
                    <span className="text-[10px] text-slate-500">Perm Secs & MDA Heads</span>
                  </div>
                </div>

                <div className="translucent-card-light p-3 rounded-2xl shadow-sm flex items-center space-x-2.5 hover:scale-[1.02] transition-all">
                  <div className="w-8 h-8 rounded-xl bg-[#0B2644] text-white flex items-center justify-center font-bold text-xs shrink-0 font-sc">
                    CFO
                  </div>
                  <div>
                    <span className="text-xs font-bold text-slate-900 block font-sc">Finance Directors</span>
                    <span className="text-[10px] text-slate-500">ICAN / CIBN Professionals</span>
                  </div>
                </div>

                <div className="translucent-card-light p-3 rounded-2xl shadow-sm flex items-center space-x-2.5 hover:scale-[1.02] transition-all">
                  <div className="w-8 h-8 rounded-xl bg-[#0B2644] text-white flex items-center justify-center font-bold text-xs shrink-0 font-sc">
                    APP
                  </div>
                  <div>
                    <span className="text-xs font-bold text-slate-900 block font-sc">Policy Executives</span>
                    <span className="text-[10px] text-slate-500">Political Appointees & Advisors</span>
                  </div>
                </div>
              </div>

            </div>
          </section>

          {/* ================= SECTION 4: TWO-COLUMN MAIN ADVERT BODY ================= */}
          <div className="p-3.5 sm:p-6 lg:p-10 grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-10">
            
            {/* LEFT COLUMN: PROGRAMMES ON OFFER (Exact 3 Disciplines from Advert) */}
            <div className="lg:col-span-7 space-y-6">
              
              <div className="border-b-2 border-[#7A470E] pb-3 flex flex-wrap items-center justify-between gap-2">
                <div>
                  <h3 className="text-xl sm:text-3xl font-black text-[#0B2644] font-sc">
                    Programmes on Offer
                  </h3>
                  <p className="text-xs text-slate-500 mt-0.5">
                    Official Executive, Postgraduate & Professional Tracks
                  </p>
                </div>
                <span className="text-xs font-bold text-[#7A470E] bg-amber-50 px-2.5 py-1 rounded-full border border-amber-200 font-sc">
                  13 Specialized Tracks
                </span>
              </div>

              {/* Discipline Switcher Buttons (Filterable for desktop interactivity) */}
              <div className="flex flex-wrap gap-1.5 print:hidden">
                {disciplines.map(d => (
                  <button
                    key={d.id}
                    onClick={() => setSelectedDiscipline(d.id)}
                    className={`px-3 py-1.5 rounded-xl text-xs font-bold transition-all font-sc cursor-pointer ${
                      selectedDiscipline === d.id
                        ? 'bg-[#0B2644] text-white shadow-sm ring-2 ring-[#7A470E]/50'
                        : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                    }`}
                  >
                    {d.label}
                  </button>
                ))}
              </div>

              {/* Programmes Listing */}
              <div className="space-y-4">
                
                {/* 1. BUSINESS ADMINISTRATION */}
                {(selectedDiscipline === 'ALL' || selectedDiscipline === 'BA') && (
                  <div className="bg-white rounded-2xl border-2 border-slate-200 p-4 sm:p-5 shadow-sm space-y-3">
                    <div className="flex items-center justify-between border-b border-slate-100 pb-2">
                      <div className="flex items-center space-x-2">
                        <span className="w-2.5 h-2.5 rounded-full bg-[#7A470E]" />
                        <h4 className="text-base font-black text-[#0B2644] uppercase tracking-wider font-sc">
                          Business Administration
                        </h4>
                      </div>
                      <span className="text-[10px] font-mono font-bold bg-slate-100 text-[#0B2644] px-2 py-0.5 rounded">
                        5 Tracks
                      </span>
                    </div>

                    <ul className="space-y-2.5 text-xs text-slate-700">
                      <li className="flex flex-col sm:flex-row sm:items-start justify-between gap-1 sm:gap-2 p-2 rounded-xl hover:bg-amber-50/50 transition-colors">
                        <div className="min-w-0 flex-1">
                          <div className="font-bold text-slate-900 flex items-center">
                            <span className="text-[#7A470E] mr-2 shrink-0">▸</span>
                            <span>Doctor of Business Administration (DBA)</span>
                          </div>
                          <p className="text-[11px] text-slate-500 pl-4 mt-0.5">
                            Terminal degree for CEOs, MDs & directors. 54-credit executive curriculum with research focus.
                          </p>
                        </div>
                        <span className="text-[10px] font-black text-[#0B2644] bg-slate-100 px-2 py-0.5 rounded font-sc self-start sm:self-auto shrink-0">
                          36 Months
                        </span>
                      </li>

                      <li className="flex flex-col sm:flex-row sm:items-start justify-between gap-1 sm:gap-2 p-2 rounded-xl hover:bg-amber-50/50 transition-colors">
                        <div className="min-w-0 flex-1">
                          <div className="font-bold text-slate-900 flex items-center">
                            <span className="text-[#7A470E] mr-2 shrink-0">▸</span>
                            <span>Master of Business Administration (MBA)</span>
                          </div>
                          <p className="text-[11px] text-slate-500 pl-4 mt-0.5">
                            Flagship executive master's degree for corporate management & entrepreneurship.
                          </p>
                        </div>
                        <span className="text-[10px] font-black text-[#0B2644] bg-slate-100 px-2 py-0.5 rounded font-sc self-start sm:self-auto shrink-0">
                          24 Months
                        </span>
                      </li>

                      <li className="flex flex-col sm:flex-row sm:items-start justify-between gap-1 sm:gap-2 p-2 rounded-xl hover:bg-amber-50/50 transition-colors">
                        <div className="min-w-0 flex-1">
                          <div className="font-bold text-slate-900 flex items-center">
                            <span className="text-[#7A470E] mr-2 shrink-0">▸</span>
                            <span>Postgraduate Diploma in Business Administration (PGD)</span>
                          </div>
                          <p className="text-[11px] text-slate-500 pl-4 mt-0.5">
                            Bridge programme for HND holders and non-business graduates seeking direct MBA admission.
                          </p>
                        </div>
                        <span className="text-[10px] font-black text-[#0B2644] bg-slate-100 px-2 py-0.5 rounded font-sc self-start sm:self-auto shrink-0">
                          12 Months
                        </span>
                      </li>

                      <li className="flex flex-col sm:flex-row sm:items-start justify-between gap-1 sm:gap-2 p-2 rounded-xl hover:bg-amber-50/50 transition-colors">
                        <div className="min-w-0 flex-1">
                          <div className="font-bold text-slate-900 flex items-center">
                            <span className="text-[#7A470E] mr-2 shrink-0">▸</span>
                            <span>Diploma in Business Administration</span>
                          </div>
                          <p className="text-[11px] text-slate-500 pl-4 mt-0.5">
                            Undergraduate diploma in core operations, office systems and enterprise principles.
                          </p>
                        </div>
                        <span className="text-[10px] font-black text-[#0B2644] bg-slate-100 px-2 py-0.5 rounded font-sc self-start sm:self-auto shrink-0">
                          18 Months
                        </span>
                      </li>

                      <li className="flex flex-col sm:flex-row sm:items-start justify-between gap-1 sm:gap-2 p-2 rounded-xl hover:bg-amber-50/50 transition-colors">
                        <div className="min-w-0 flex-1">
                          <div className="font-bold text-slate-900 flex items-center">
                            <span className="text-[#7A470E] mr-2 shrink-0">▸</span>
                            <span>Certificate in Business Administration</span>
                          </div>
                          <p className="text-[11px] text-slate-500 pl-4 mt-0.5">
                            Intensive professional certificate for administrative and managerial competence.
                          </p>
                        </div>
                        <span className="text-[10px] font-black text-[#0B2644] bg-slate-100 px-2 py-0.5 rounded font-sc self-start sm:self-auto shrink-0">
                          6 Months
                        </span>
                      </li>
                    </ul>
                  </div>
                )}

                {/* 2. PUBLIC ADMINISTRATION */}
                {(selectedDiscipline === 'ALL' || selectedDiscipline === 'PA') && (
                  <div className="bg-white rounded-2xl border-2 border-slate-200 p-4 sm:p-5 shadow-sm space-y-3">
                    <div className="flex items-center justify-between border-b border-slate-100 pb-2">
                      <div className="flex items-center space-x-2">
                        <span className="w-2.5 h-2.5 rounded-full bg-[#7A470E]" />
                        <h4 className="text-base font-black text-[#0B2644] uppercase tracking-wider font-sc">
                          Public Administration
                        </h4>
                      </div>
                      <span className="text-[10px] font-mono font-bold bg-slate-100 text-[#0B2644] px-2 py-0.5 rounded">
                        4 Tracks
                      </span>
                    </div>

                    <ul className="space-y-2.5 text-xs text-slate-700">
                      <li className="flex flex-col sm:flex-row sm:items-start justify-between gap-1 sm:gap-2 p-2 rounded-xl hover:bg-amber-50/50 transition-colors">
                        <div className="min-w-0 flex-1">
                          <div className="font-bold text-slate-900 flex items-center">
                            <span className="text-[#7A470E] mr-2 shrink-0">▸</span>
                            <span>Doctor of Public Administration (DPA)</span>
                          </div>
                          <p className="text-[11px] text-slate-500 pl-4 mt-0.5">
                            Senior doctorate for permanent secretaries, commissioners, and public enterprise leaders.
                          </p>
                        </div>
                        <span className="text-[10px] font-black text-[#0B2644] bg-slate-100 px-2 py-0.5 rounded font-sc self-start sm:self-auto shrink-0">
                          36 Months
                        </span>
                      </li>

                      <li className="flex flex-col sm:flex-row sm:items-start justify-between gap-1 sm:gap-2 p-2 rounded-xl hover:bg-amber-50/50 transition-colors">
                        <div className="min-w-0 flex-1">
                          <div className="font-bold text-slate-900 flex items-center">
                            <span className="text-[#7A470E] mr-2 shrink-0">▸</span>
                            <span>Executive Master of Public Administration (EMPA)</span>
                          </div>
                          <p className="text-[11px] text-slate-500 pl-4 mt-0.5">
                            Tailored for senior civil servants, parastatal heads and public policy advisors.
                          </p>
                        </div>
                        <span className="text-[10px] font-black text-[#0B2644] bg-slate-100 px-2 py-0.5 rounded font-sc self-start sm:self-auto shrink-0">
                          24 Months
                        </span>
                      </li>

                      <li className="flex flex-col sm:flex-row sm:items-start justify-between gap-1 sm:gap-2 p-2 rounded-xl hover:bg-amber-50/50 transition-colors">
                        <div className="min-w-0 flex-1">
                          <div className="font-bold text-slate-900 flex items-center">
                            <span className="text-[#7A470E] mr-2 shrink-0">▸</span>
                            <span>Professional Diploma in Public Administration</span>
                          </div>
                          <p className="text-[11px] text-slate-500 pl-4 mt-0.5">
                            Civil service promotion and professional accreditation qualification.
                          </p>
                        </div>
                        <span className="text-[10px] font-black text-[#0B2644] bg-slate-100 px-2 py-0.5 rounded font-sc self-start sm:self-auto shrink-0">
                          12-18 Mos
                        </span>
                      </li>

                      <li className="flex flex-col sm:flex-row sm:items-start justify-between gap-1 sm:gap-2 p-2 rounded-xl hover:bg-amber-50/50 transition-colors">
                        <div className="min-w-0 flex-1">
                          <div className="font-bold text-slate-900 flex items-center">
                            <span className="text-[#7A470E] mr-2 shrink-0">▸</span>
                            <span>Professional Certificate in Public Administration</span>
                          </div>
                          <p className="text-[11px] text-slate-500 pl-4 mt-0.5">
                            Practical immersion in civil service regulations, administrative protocols, and memo drafting.
                          </p>
                        </div>
                        <span className="text-[10px] font-black text-[#0B2644] bg-slate-100 px-2 py-0.5 rounded font-sc self-start sm:self-auto shrink-0">
                          6 Months
                        </span>
                      </li>
                    </ul>
                  </div>
                )}

                {/* 3. ACCOUNTING AND FINANCE */}
                {(selectedDiscipline === 'ALL' || selectedDiscipline === 'AF') && (
                  <div className="bg-white rounded-2xl border-2 border-slate-200 p-4 sm:p-5 shadow-sm space-y-3">
                    <div className="flex items-center justify-between border-b border-slate-100 pb-2">
                      <div className="flex items-center space-x-2">
                        <span className="w-2.5 h-2.5 rounded-full bg-[#7A470E]" />
                        <h4 className="text-base font-black text-[#0B2644] uppercase tracking-wider font-sc">
                          Accounting and Finance
                        </h4>
                      </div>
                      <span className="text-[10px] font-mono font-bold bg-slate-100 text-[#0B2644] px-2 py-0.5 rounded">
                        4 Tracks
                      </span>
                    </div>

                    <ul className="space-y-2.5 text-xs text-slate-700">
                      <li className="flex flex-col sm:flex-row sm:items-start justify-between gap-1 sm:gap-2 p-2 rounded-xl hover:bg-amber-50/50 transition-colors">
                        <div className="min-w-0 flex-1">
                          <div className="font-bold text-slate-900 flex items-center">
                            <span className="text-[#7A470E] mr-2 shrink-0">▸</span>
                            <span>Doctor of Accounting and Finance</span>
                          </div>
                          <p className="text-[11px] text-slate-500 pl-4 mt-0.5">
                            Terminal research doctorate in forensic accounting, fiscal policy and corporate valuation.
                          </p>
                        </div>
                        <span className="text-[10px] font-black text-[#0B2644] bg-slate-100 px-2 py-0.5 rounded font-sc self-start sm:self-auto shrink-0">
                          36 Months
                        </span>
                      </li>

                      <li className="flex flex-col sm:flex-row sm:items-start justify-between gap-1 sm:gap-2 p-2 rounded-xl hover:bg-amber-50/50 transition-colors">
                        <div className="min-w-0 flex-1">
                          <div className="font-bold text-slate-900 flex items-center">
                            <span className="text-[#7A470E] mr-2 shrink-0">▸</span>
                            <span>Executive Master of Accounting and Finance</span>
                          </div>
                          <p className="text-[11px] text-slate-500 pl-4 mt-0.5">
                            Strategic financial leadership, IPSAS, IFRS reporting, corporate treasury and taxation.
                          </p>
                        </div>
                        <span className="text-[10px] font-black text-[#0B2644] bg-slate-100 px-2 py-0.5 rounded font-sc self-start sm:self-auto shrink-0">
                          24 Months
                        </span>
                      </li>

                      <li className="flex flex-col sm:flex-row sm:items-start justify-between gap-1 sm:gap-2 p-2 rounded-xl hover:bg-amber-50/50 transition-colors">
                        <div className="min-w-0 flex-1">
                          <div className="font-bold text-slate-900 flex items-center">
                            <span className="text-[#7A470E] mr-2 shrink-0">▸</span>
                            <span>Professional Diploma in Accounting and Finance</span>
                          </div>
                          <p className="text-[11px] text-slate-500 pl-4 mt-0.5">
                            ICAN and CIBN affiliated route for professional accountants and auditors.
                          </p>
                        </div>
                        <span className="text-[10px] font-black text-[#0B2644] bg-slate-100 px-2 py-0.5 rounded font-sc self-start sm:self-auto shrink-0">
                          12-18 Mos
                        </span>
                      </li>

                      <li className="flex flex-col sm:flex-row sm:items-start justify-between gap-1 sm:gap-2 p-2 rounded-xl hover:bg-amber-50/50 transition-colors">
                        <div className="min-w-0 flex-1">
                          <div className="font-bold text-slate-900 flex items-center">
                            <span className="text-[#7A470E] mr-2 shrink-0">▸</span>
                            <span>Professional Certificate in Accounting and Finance</span>
                          </div>
                          <p className="text-[11px] text-slate-500 pl-4 mt-0.5">
                            Essential bookkeeping, statutory deductions (PAYE, VAT, WHT), and computerized accounts.
                          </p>
                        </div>
                        <span className="text-[10px] font-black text-[#0B2644] bg-slate-100 px-2 py-0.5 rounded font-sc self-start sm:self-auto shrink-0">
                          6 Months
                        </span>
                      </li>
                    </ul>
                  </div>
                )}

              </div>

            </div>

            {/* RIGHT COLUMN: ADMISSION REQUIREMENTS (Exact (a) to (e) from Advert) */}
            <div className="lg:col-span-5 space-y-6">
              
              <div className="border-b-2 border-[#7A470E] pb-3">
                <h3 className="text-2xl sm:text-3xl font-black text-[#0B2644] font-sc">
                  Admission Requirements
                </h3>
                <p className="text-xs text-slate-500 mt-0.5">
                  Academic & Professional Entry Prerequisites
                </p>
              </div>

              {/* Exact Itemized Requirements List from Advert */}
              <div className="space-y-3.5">
                {admissionRequirements.map((req) => (
                  <div 
                    key={req.letter}
                    className="bg-white rounded-2xl border-2 border-slate-200 p-4 shadow-sm hover:border-[#7A470E] transition-all relative overflow-hidden group"
                  >
                    <div className="flex items-start space-x-3">
                      <div className="w-7 h-7 rounded-lg bg-[#0B2644] text-white font-black text-xs flex items-center justify-center font-sc shrink-0 mt-0.5 shadow-sm">
                        ({req.letter})
                      </div>
                      <div className="space-y-1">
                        <span className="text-[11px] font-bold text-[#7A470E] uppercase tracking-wider font-sc block">
                          {req.badge}
                        </span>
                        <p className="text-xs font-semibold text-slate-800 leading-relaxed">
                          {req.criteria}
                        </p>
                        <p className="text-[11px] text-slate-500 leading-normal pt-1 border-t border-slate-100">
                          {req.details}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Exact italic guidance from Advert */}
              <div className="bg-slate-100 p-4 rounded-2xl border-l-4 border-[#7A470E] text-xs text-slate-700 italic">
                "Candidates may apply to any track that matches their qualifications and career goals."
              </div>

              {/* University Governance Notice on Admission */}
              <div className="bg-amber-50 p-4 rounded-2xl border border-amber-300 text-xs text-amber-950 space-y-1 shadow-2xs">
                <div className="flex items-center space-x-1.5 font-bold font-sc uppercase tracking-wider text-[#7A470E] text-[11px]">
                  <AlertCircle className="w-3.5 h-3.5 shrink-0" />
                  <span>Academic Governance & Application Notice</span>
                </div>
                <p className="leading-relaxed text-[11.5px] text-slate-800 font-medium">
                  Possession of basic entry prerequisites qualifies a candidate for screening and consideration, but does <strong>NOT</strong> guarantee an offer of admission. Final selection is competitive, merit-based, and subject to authentic credential validation, transcript audit, and University Senate approval.
                </p>
              </div>

              {/* Delivery Mechanism Highlights */}
              <div className="bg-[#06182B] text-white p-5 rounded-2xl border border-[#7A470E]/40 space-y-2.5">
                <div className="text-xs uppercase font-extrabold tracking-wider text-[#B87A28] font-sc flex items-center">
                  <Clock className="w-3.5 h-3.5 mr-1.5 text-[#7A470E]" />
                  <span>Executive Delivery Model</span>
                </div>
                <ul className="text-xs text-slate-300 space-y-1.5 pl-1">
                  <li className="flex items-center space-x-2">
                    <CheckCircle2 className="w-3 h-3 text-[#7A470E] shrink-0" />
                    <span>Full-time / Virtual / Hybrid Modules (Interactive Digital LMS & Campus Masterclasses)</span>
                  </li>
                  <li className="flex items-center space-x-2">
                    <CheckCircle2 className="w-3 h-3 text-[#7A470E] shrink-0" />
                    <span>Live Interactive Hybrid Broadcasting via LMS</span>
                  </li>
                  <li className="flex items-center space-x-2">
                    <CheckCircle2 className="w-3 h-3 text-[#7A470E] shrink-0" />
                    <span>Boardroom Case Studies & Practical Masterclasses</span>
                  </li>
                </ul>
              </div>

            </div>

          </div>

          {/* ================= SECTION 5: LOWER DARK NAVY ENQUIRIES BAND ================= */}
          <footer className="bg-[#06182B] text-white p-6 sm:p-8 border-t-4 border-[#7A470E]">
            <div className="max-w-4xl mx-auto space-y-6">
              
              <div className="text-center">
                <span className="text-xs font-black tracking-widest uppercase text-[#B87A28] font-sc">
                  Official Communication Channels
                </span>
                <h4 className="text-xl sm:text-2xl font-black text-white font-sc mt-0.5">
                  ENQUIRIES & APPLICATIONS
                </h4>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-2">
                {/* Telephone Channels */}
                <div className="bg-[#0B2644] p-4 rounded-2xl border border-[#7A470E]/30 space-y-3">
                  <div className="flex items-center space-x-2 text-xs font-bold text-[#B87A28] font-sc">
                    <Phone className="w-4 h-4 text-[#7A470E]" />
                    <span>Telephone & Messaging Desks</span>
                  </div>

                  <div className="space-y-2 text-xs">
                    <div>
                      <span className="text-slate-400 block text-[11px]">Primary WhatsApp Admissions Desk:</span>
                      <a 
                        href="https://wa.me/2348033380401" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-white font-bold hover:text-[#B87A28] transition-colors font-mono text-sm inline-flex items-center mt-0.5"
                      >
                        +234 803 338 0401 
                        <span className="text-[10px] ml-2 text-emerald-400 bg-emerald-950 px-2 py-0.5 rounded border border-emerald-800">
                          (WhatsApp only)
                        </span>
                      </a>
                    </div>

                    <div>
                      <span className="text-slate-400 block text-[11px]">Voice & SMS Inquiries:</span>
                      <a 
                        href="tel:+2347012550607" 
                        className="text-white font-bold hover:text-[#B87A28] transition-colors font-mono text-sm inline-flex items-center mt-0.5"
                      >
                        +234 701 255 0607
                        <span className="text-[10px] ml-2 text-amber-300 bg-amber-950 px-2 py-0.5 rounded border border-amber-800">
                          (Calls & SMS only)
                        </span>
                      </a>
                    </div>
                  </div>
                </div>

                {/* Email Channels */}
                <div className="bg-[#0B2644] p-4 rounded-2xl border border-[#7A470E]/30 space-y-3">
                  <div className="flex items-center space-x-2 text-xs font-bold text-[#B87A28] font-sc">
                    <Mail className="w-4 h-4 text-[#7A470E]" />
                    <span>Official Institutional Email Addresses</span>
                  </div>

                  <div className="space-y-2 text-xs">
                    <div>
                      <span className="text-slate-400 block text-[11px]">General Business School Admissions:</span>
                      <a 
                        href="mailto:aisubs@aksu.edu.ng" 
                        className="text-white font-bold hover:text-[#B87A28] transition-colors font-mono text-sm mt-0.5 block"
                      >
                        aisubs@aksu.edu.ng
                      </a>
                    </div>

                    <div>
                      <span className="text-slate-400 block text-[11px]">Directorate / Director's Direct Inquiries:</span>
                      <a 
                        href="mailto:uwemuwah@aksu.edu.ng" 
                        className="text-white font-bold hover:text-[#B87A28] transition-colors font-mono text-sm mt-0.5 block"
                      >
                        uwemuwah@aksu.edu.ng
                      </a>
                    </div>
                  </div>
                </div>
              </div>

              {/* Directorate Validation & Main Campus Location */}
              <div className="pt-4 border-t border-slate-700/80 flex flex-wrap items-center justify-between gap-4 text-xs">
                <div>
                  <span className="text-[11px] text-slate-400 block">Directorate / Office of the Director:</span>
                  <span className="text-white font-black font-sc text-sm">
                    Dr Uwem Etim Uwah, <span className="text-[#B87A28] font-bold">PhD, FCA, ACTI</span>
                  </span>
                  <span className="text-slate-400 block text-[11px]">Acting Director, AKSU Business School</span>
                </div>

                <div className="flex flex-col items-start sm:items-center space-y-1">
                  <span className="text-[10px] text-slate-400 font-sc uppercase tracking-wider">Official Channels:</span>
                  <SocialIconLinks theme="footer" iconClassName="w-3.5 h-3.5" />
                </div>

                <div className="text-right sm:text-right text-left">
                  <span className="text-[11px] text-[#B87A28] font-bold font-sc block">Official Campus Location:</span>
                  <span className="text-white font-semibold block text-xs">
                    Main Campus in Ikot Akpaden, Mkpat Enin LGA
                  </span>
                  <span className="text-slate-400 text-[11px]">Akwa Ibom State, Nigeria</span>
                </div>
              </div>

            </div>
          </footer>

          {/* ================= SECTION 6: BOTTOM GOLD LECTURE COMMENCEMENT BANNER ================= */}
          <div className="bg-[#7A470E] text-white py-5 px-6 sm:px-10 text-center border-t-2 border-[#5C3306]">
            <h3 className="text-2xl sm:text-4xl font-black font-sc tracking-wide">
              October 2026 Commencement
            </h3>
            <p className="text-sm sm:text-base font-black tracking-widest uppercase font-sc mt-1 text-amber-100">
              Full-time / Virtual / Hybrid Modules — Admissions Open!
            </p>

            <div className="mt-4 flex flex-wrap items-center justify-center gap-3 print:hidden">
              <button
                onClick={() => setActiveView('apply')}
                className="px-6 py-2.5 rounded-xl bg-[#06182B] hover:bg-[#0B2644] text-white font-bold text-sm shadow-xl hover:scale-105 transition-all flex items-center space-x-2 font-sc cursor-pointer"
              >
                <Award className="w-4 h-4 text-[#B87A28]" />
                <span>Begin Admission Application (₦25,000)</span>
                <ArrowRight className="w-4 h-4 text-[#B87A28]" />
              </button>

              <button
                onClick={() => setActiveView('screening')}
                className="px-5 py-2.5 rounded-xl bg-white/90 hover:bg-white text-[#06182B] font-bold text-sm shadow transition-all font-sc cursor-pointer"
              >
                <span>Track Application / Screening Portal</span>
              </button>
            </div>
          </div>

        </article>
      </div>

    </div>
  );
};
